import { useAppSelector } from "../../redux/store/hooks"
import {
    selectDashboardCurrentPatientData,
    selectDashboardSelectedValueType
} from "../../redux/ProfessionalDashboard"
import notNull from "../../util/notNull"
import {
    ActiveValueType,
    getAbsoluteSeverityColorLimits,
    GetPatientDataCurrentResponsePayload,
    getSeverityColor,
    getSeverityColorPalette,
    getSeverityScore,
    isActiveValueType,
    SeverityScore,
    Value,
    ValueType,
    ValueTypeMaxValues
} from "pokal_api"
import Card from "../Card"
import SmallHeadingComponent from "../SmallHeadingComponent"
import { useEffect, useState } from "react"
import { CategoryBar } from "@tremor/react"
import PokalColorConverter from "../../util/PokalColorConverter"
import severityToString from "../../util/severityToString"
import {
    hidePatientDetailViewDataLatest,
    mockPatientUsed,
    mockPhqValueTypeUsed,
    mockSleepValueTypeUsed,
    selectOnboardingIsRunningState,
    selectOnboardingStep
} from "../../redux/Onboarding"
import {
    MOCK_PHQ_CURRENT_DATA,
    MOCK_SLEEP_CURRENT_DATA
} from "../../onboarding/mock/mockPatientData"
import DocumentationComponent from "../DocumentationComponent"
import { LATEST_VALUE_DOC, TREND_DOC } from "../../documentation/Documentation"

export function getMarkerValue(x: number, type: ActiveValueType) {
    if (type === ValueType.MOOD) return x + 0.5
    return x
}

function LatestDataView() {
    const fetchedSelectedValueType = useAppSelector(
        selectDashboardSelectedValueType
    )
    const fetchedCurrentData = useAppSelector(selectDashboardCurrentPatientData)

    const [selectedValueType, setSelectedValueType] =
        useState<ValueType | null>(null)
    const [currentData, setCurrentData] =
        useState<GetPatientDataCurrentResponsePayload | null>(null)

    const step = useAppSelector(selectOnboardingStep)
    const running = useAppSelector(selectOnboardingIsRunningState)

    useEffect(() => {
        if (mockPatientUsed(running, step)) {
            if (mockPhqValueTypeUsed(running, step)) {
                setCurrentData(MOCK_PHQ_CURRENT_DATA)
                setSelectedValueType(ValueType.PHQUESTIONNAIRE)
            }
            if (mockSleepValueTypeUsed(running, step)) {
                setCurrentData(MOCK_SLEEP_CURRENT_DATA)
                setSelectedValueType(ValueType.SLEEP_DURATION)
            }
        } else {
            setCurrentData(fetchedCurrentData)
            setSelectedValueType(fetchedSelectedValueType)
        }
    }, [fetchedCurrentData, fetchedSelectedValueType, running, step])

    const [latest, setLatest] = useState<Value | null>(null)
    const [severityScore, setSeverityScore] = useState<SeverityScore | null>(
        null
    )
    const [borderColor, setBorderColor] = useState<null | string>(null)
    const [severityText, setSeverityText] = useState<null | string>(null)

    useEffect(() => {
        if (
            notNull(currentData) &&
            currentData.length > 0 &&
            notNull(selectedValueType) &&
            isActiveValueType(selectedValueType)
        ) {
            const latest = [...currentData].sort((d1, d2) => new Date(d2.date).getTime() - new Date(d1.date).getTime())[0]
            setLatest(latest)
            try {
                setSeverityScore(
                    getSeverityScore(
                        latest,
                        selectedValueType as ActiveValueType
                    )
                )
            } catch {}
        } else setLatest(null)
    }, [currentData, selectedValueType])

    useEffect(() => {
        if (
            notNull(severityScore) &&
            notNull(selectedValueType) &&
            isActiveValueType(selectedValueType)
        ) {
            try {
                setBorderColor(
                    PokalColorConverter.toTopBorder(
                        getSeverityColor(
                            severityScore,
                            selectedValueType as ActiveValueType
                        )
                    )
                )
                setSeverityText(
                    severityToString(
                        severityScore,
                        selectedValueType as ActiveValueType
                    )
                )
            } catch {}
        }
    }, [severityScore])

    return (
        <>
            {notNull(selectedValueType) &&
                notNull(latest) &&
                isActiveValueType(selectedValueType) &&
                notNull(severityScore) && (
                    <div className={"flex-1 flex flex-col"}>
                        <Card
                            blur={hidePatientDetailViewDataLatest(
                                running,
                                step
                            )}
                            outline={
                                notNull(borderColor) ? borderColor : undefined
                            }
                            fill={true}
                            width={"100%"}>
                            <div className={"flex flex-col space-y-4"}>
                                <SmallHeadingComponent>
                                    Aktuellste Daten
                                </SmallHeadingComponent>
                                <div>
                                    <DocumentationComponent
                                        documentation={LATEST_VALUE_DOC}>
                                        Aktuellster Wert
                                    </DocumentationComponent>
                                    :{" "}
                                    {notNull(severityText) && (
                                        <span className={"font-bold"}>
                                            {severityText}
                                        </span>
                                    )}
                                </div>
                                <CategoryBar
                                    values={getAbsoluteSeverityColorLimits(
                                        selectedValueType as ActiveValueType
                                    )}
                                    showLabels={false}
                                    colors={getSeverityColorPalette(
                                        selectedValueType as ActiveValueType
                                    )}
                                    markerValue={getMarkerValue(
                                        latest.value,
                                        selectedValueType as ActiveValueType
                                    )}
                                />
                                <div>
                                    <span className={"font-bold text-3xl"}>
                                        {latest.value}
                                    </span>{" "}
                                    von{" "}
                                    <span>
                                        {
                                            ValueTypeMaxValues[
                                                selectedValueType as ActiveValueType
                                            ]
                                        }
                                    </span>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}
        </>
    )
}

export default LatestDataView
